import { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { ALARM_SOUND_OPTIONS, DEFAULT_ALARM_SOUND } from "../../constants/misc";
import { stopAlarmSound } from "../../utils/alarmSound";
import { reminderLabel } from "../../utils/reminderFormat";
import { nextReminders, reminderLimitHint, alarmLocked, canSwitchToAlarm, ALARM_LIMIT } from "../../utils/reminderLimits";
import { usePremium } from "../../firebase/billing";
import { LockBadge } from "../LockBadge";
import { AlarmSoundRow, CustomSoundRow } from "./AlarmSoundRow";
import { useAlarmPermissionState, requestAlarmPermission } from "../../capacitor/alarmPermissions";

// Shown right where someone picks ⏰ Alarm, because that's the moment they're
// forming the expectation that it'll ring. Every one of these permissions
// fails silently at fire time — the alarm is scheduled, scheduleAll resolves,
// and nothing happens — so an unmissable warning here is the difference
// between a missed alarm and a two-tap fix. See alarmPermissions.js.
function AlarmPermissionWarnings({ problems }) {
  const [errors, setErrors] = useState({});
  const openSettings = async (key) => {
    const err = await requestAlarmPermission(key);
    setErrors((p) => ({ ...p, [key]: err }));
  };
  if (!problems.length) return null;
  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
      {problems.map((p) => (
        <div key={p.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "2px 0" }}>
          <span style={{ fontSize: 13, lineHeight: 1.25 }} aria-hidden="true">⚠️</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text, #2d2233)" }}>{p.title}</div>
            {p.body && <div style={{ fontSize: 11, lineHeight: 1.35, color: "var(--c-text-mid)", marginTop: 1 }}>{p.body}</div>}
            {errors[p.key] && (
              <div style={{ fontSize: 10.5, lineHeight: 1.3, color: "var(--c-text-accent, #b3577e)", marginTop: 3, fontWeight: 600 }}>
                Couldn't open Settings ({errors[p.key]}). Open it yourself: Settings → Apps → Clarity.
              </div>
            )}
          </div>
          <button type="button" onClick={() => openSettings(p.key)}
            style={{ flexShrink: 0, padding: "4px 10px", borderRadius: 999, border: "none", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            Fix
          </button>
        </div>
      ))}
    </div>
  );
}

// Reminder offsets (minutes-before). Maps straight to Google Calendar reminders.
// Fine-grained custom entry, minutes/hours only — day/week reminders go
// through DayWeekReminderAdd below, which caps the range instead of letting
// someone type an unbounded number of days.
function CustomReminderAdd({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [unit, setUnit] = useState("min");
  const add = () => {
    const n = parseInt(val, 10);
    if (!n || n < 0) return;
    onAdd(unit === "min" ? n : n * 60);
    setVal(""); setOpen(false);
  };
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} style={{ padding: "5px 11px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "1px dashed var(--c-border)", color: "var(--c-text-mid)", background: "transparent" }}>＋ Custom</button>
  );
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <input type="number" min="0" value={val} autoFocus onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} placeholder="e.g. 45" style={{ width: 64, padding: "5px 8px", borderRadius: 8, border: "1px solid var(--c-border)", fontSize: 12 }} />
      <select value={unit} onChange={e => setUnit(e.target.value)} style={{ padding: "5px 6px", borderRadius: 8, border: "1px solid var(--c-border)", fontSize: 12, cursor: "pointer" }}>
        <option value="min">min</option><option value="hr">hr</option>
      </select>
      <button type="button" onClick={add} style={{ padding: "5px 9px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Add</button>
    </span>
  );
}

// Day/week reminders, e.g. "3 days before" or "2 weeks before" — a capped
// stepper instead of free-text so nobody ends up with a reminder set further
// out than the app can usefully schedule. Caps (14 days / 4 weeks) match
// Google Calendar's own outer limit of 4 weeks for any reminder.
function DayWeekReminderAdd({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("day");
  const [n, setN] = useState(1);
  const max = mode === "day" ? 14 : 4;
  const switchMode = (m) => { setMode(m); setN(1); };
  const step = (delta) => setN(v => Math.min(Math.max(v + delta, 1), max));
  const add = () => {
    onAdd(mode === "day" ? n * 1440 : n * 10080);
    setMode("day"); setN(1); setOpen(false);
  };
  const segBtn = (on) => ({ padding: "5px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none", color: on ? "#fff" : "var(--c-text-mid)", background: on ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "var(--c-bg-card)" });
  const stepBtn = { width: 22, height: 22, borderRadius: "50%", border: "1px solid var(--c-border)", background: "var(--c-bg-card)", color: "var(--c-text-mid)", fontSize: 14, fontWeight: 700, lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} style={{ padding: "5px 11px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "1px dashed var(--c-border)", color: "var(--c-text-mid)", background: "transparent" }}>＋ Day/Week before</button>
  );
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ display: "inline-flex", borderRadius: 999, overflow: "hidden", border: "1px solid var(--c-border)" }}>
        <button type="button" onClick={() => switchMode("day")} style={segBtn(mode === "day")}>Day</button>
        <button type="button" onClick={() => switchMode("week")} style={segBtn(mode === "week")}>Week</button>
      </span>
      <button type="button" onClick={() => step(-1)} style={stepBtn}>–</button>
      <span style={{ minWidth: 16, textAlign: "center", fontSize: 12, fontWeight: 700 }}>{n}</span>
      <button type="button" onClick={() => step(1)} style={stepBtn}>+</button>
      <span style={{ fontSize: 11, color: "var(--c-text-mid)" }}>(max {max})</span>
      <button type="button" onClick={add} style={{ padding: "5px 9px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Add</button>
    </span>
  );
}

// alertType/onAlertTypeChange + alarmSound/onAlarmSoundChange are optional — when
// omitted (or once at least one reminder offset is picked and a handler exists),
// callers get the extra Notification/Alarm row for free. Notification always
// plays the one fixed sound (no options); Alarm shows ALARM_SOUND_OPTIONS with a
// preview button per choice.
// onlyDayWeek: the item has no clock time, so every sub-day offset ("At start",
// "10 min before", a custom minutes/hours entry) has nothing to count back
// from — only whole day/week offsets mean anything, and they fire at
// UNTIMED_REMINDER_HOUR (9:00 AM) on the reminder day. Set a time and the full
// preset row comes back on its own.
export function ReminderPicker({ value = [], onChange, alertType = "notification", onAlertTypeChange, alarmSound = DEFAULT_ALARM_SOUND, onAlarmSoundChange, alarmSoundUri = "", alarmSoundLabel = "", onCustomSoundChange, hideDayWeek = false, onlyDayWeek = false }) {
  const presets = onlyDayWeek ? [] : [0, 5, 10, 15, 30, 60];
  const sel = Array.isArray(value) ? value : [];
  const labelFor = reminderLabel;
  // Notifications: free up to five per item, Premium unlimited. The Alarm type
  // is Premium only (up to five) — see utils/reminderLimits.js. While the plan is still loading nobody is held to
  // the free limit, so a Premium user's first tap isn't wrongly refused.
  const { isPremium, loading: premiumLoading } = usePremium();
  const unlocked = isPremium || premiumLoading;
  const toggle = (m) => { const next = nextReminders(sel, m, unlocked, alertType); if (next) onChange(next); };
  const limitHint = reminderLimitHint(sel, unlocked, alertType);
  // Free accounts can't pick Alarm (an item that is already an alarm keeps it);
  // and an item with more reminders than an alarm allows must be trimmed first.
  const alarmIsLocked = alarmLocked(unlocked, alertType);
  const tooManyForAlarm = unlocked && alertType !== "alarm" && !canSwitchToAlarm(sel, unlocked);
  // In onlyDayWeek mode a stale sub-day offset (picked while a time was set,
  // then the time removed) would otherwise render as an un-labelled chip for
  // something that can never fire — only surface what's actually schedulable.
  const customSel = sel.filter(m => !presets.includes(m) && (!onlyDayWeek || m >= 1440));
  const chip = (on) => ({ padding: "5px 11px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", border: on ? "none" : "1px solid var(--c-border)", color: on ? "#fff" : "var(--c-text-mid)", background: on ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "var(--c-bg-card)" });

  // alertType is one setting for the whole item, not per-offset — so a
  // day/week-away reminder can't ring as a quiet exception while a same-item
  // short offset rings as a full alarm. Once any offset is beyond an hour,
  // hide the Alarm option entirely and force the item back to Notification
  // rather than leave a stale "Alarm" selection that silently wouldn't apply.
  const hasLongOffset = sel.some(m => m > 60);
  // Only poll the OS while Alarm mode is actually on screen and selected —
  // plain notifications don't depend on any of these.
  const { problems: permissionProblems } = useAlarmPermissionState(alertType === "alarm" && sel.length > 0 && !hasLongOffset);
  useEffect(() => {
    if (hasLongOffset && alertType === "alarm" && onAlertTypeChange) onAlertTypeChange("notification");
  }, [hasLongOffset, alertType, onAlertTypeChange]);

  // Stop any preview mid-playback when this panel closes/unmounts (also
  // covers switching between reminder pickers, since only one is ever
  // mounted at a time).
  useEffect(() => () => stopAlarmSound(), []);
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        {presets.map(m => (
          <button key={m} type="button" onClick={() => toggle(m)} style={chip(sel.includes(m))}>{labelFor(m)}</button>
        ))}
        {customSel.map(m => (
          <button key={m} type="button" onClick={() => toggle(m)} style={chip(true)}>{labelFor(m)} ✕</button>
        ))}
        {!onlyDayWeek && <CustomReminderAdd onAdd={toggle} />}
        {!hideDayWeek && <DayWeekReminderAdd onAdd={toggle} />}
      </div>

      {limitHint && (
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--c-text-mid)" }}>
          {!unlocked && <LockBadge size={16} />}
          <span>{limitHint}</span>
        </div>
      )}

      {/* Alarm mode needs Android's AlarmManager (via AlarmScheduler, see
          src/capacitor/alarmSync.js) for exact full-screen wake-ups — there's
          no web equivalent, so the toggle (and alarm-sound picker) only
          make sense on the native build. Web/PWA always stays "notification".
          Also hidden once a day/week offset is selected — see hasLongOffset. */}
      {sel.length > 0 && !hasLongOffset && onAlertTypeChange && Capacitor.isNativePlatform() && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" onClick={() => onAlertTypeChange("notification")} style={{ ...chip(alertType !== "alarm"), flex: 1, textAlign: "center" }}>🔔 Notification</button>
            <button type="button" aria-disabled={alarmIsLocked || tooManyForAlarm}
              onClick={() => { if (!alarmIsLocked && !tooManyForAlarm) onAlertTypeChange("alarm"); }}
              style={{ ...chip(alertType === "alarm"), flex: 1, textAlign: "center", opacity: alarmIsLocked || tooManyForAlarm ? 0.6 : 1, cursor: alarmIsLocked || tooManyForAlarm ? "default" : "pointer" }}>
              ⏰ Alarm {alarmIsLocked && <LockBadge size={14} />}
            </button>
          </div>
          {alarmIsLocked && (
            <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--c-text-mid)" }}>Alarm reminders are Premium.</div>
          )}
          {tooManyForAlarm && (
            <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--c-text-mid)" }}>Alarms allow up to {ALARM_LIMIT} reminders per item. Remove some to switch.</div>
          )}
          {alertType === "alarm" && <AlarmPermissionWarnings problems={permissionProblems} />}
          {/* Sound choice is usable again on both platforms: native alarms
              ring via Clarity's own AlarmRingtoneService (see
              AlarmSchedulerPlugin/AlarmReceiver), which does honor the
              selected sound, and the web build still uses the plain
              <audio>-based playback in utils/alarmSound.js. (A prior
              attempt handed native alarm-firing off to the phone's system
              Clock app, which always used its own default sound regardless
              of this picker — that approach was abandoned after the OEM's
              Clock app rejected the required intent outright, so this
              picker is meaningful on native again.) */}
          {alertType === "alarm" && onAlarmSoundChange && (
            <div style={{ marginTop: 8 }}>
              {onCustomSoundChange && (
                <CustomSoundRow uri={alarmSoundUri} label={alarmSoundLabel} selected={!!alarmSoundUri}
                  onPick={({ uri, label }) => onCustomSoundChange({ uri, label })}
                  onClear={() => onCustomSoundChange(null)} />
              )}
              {ALARM_SOUND_OPTIONS.map(opt => (
                <AlarmSoundRow key={opt.id} option={opt} selected={!alarmSoundUri && alarmSound === opt.id}
                  onSelect={() => { onAlarmSoundChange(opt.id); if (onCustomSoundChange) onCustomSoundChange(null); }} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
